import axios from 'axios';

export interface Employee {
  employeeCode: string;
  employeeName: string;
  workEmail: string;
  supervisorCode: string;
  supervisorName: string;
  position: string;
  department: string;
  departmentDesc: string;
  subDepartmentDesc: string;
  status: string;
}

let employeesCache: Employee[] | null = null;

export const clearEmployeeCache = (): void => {
  console.log('[EmployeeData] Clearing frontend employee cache');
  employeesCache = null;
};

export const loadEmployees = async (): Promise<Employee[]> => {
  if (employeesCache) {
    console.log('Using cached employee data:', employeesCache.length, 'employees');
    return employeesCache;
  }

  console.log('Loading employee data from API...');
  try {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://finops-portal.angel-admin.angelstudios.com';
    const response = await axios.get(`${API_BASE_URL}/api/admin/employees`, { 
      withCredentials: true 
    });

    if (response.data.success && response.data.data) {
      employeesCache = response.data.data;
      console.log('Successfully loaded', employeesCache!.length, 'active employees from API');
      return employeesCache!;
    }

    throw new Error('Failed to load employee data from API');
  } catch (error) {
    console.error('Error loading employees:', error);
    return [];
  }
};

export const getEmployeeByCode = (employees: Employee[], code: string): Employee | undefined => {
  return employees.find((emp) => emp.employeeCode === code);
};

export function getEmployeeDisplayLabel(employee: Employee): string {
  return `${employee.workEmail} - ${employee.employeeName.toLowerCase()}`;
};

export const getEmployeesByDepartment = (employees: Employee[], department: string): Employee[] => {
  return employees.filter((emp) => emp.department === department);
};

export const getEmployeesBySubDepartment = (employees: Employee[], subDepartment: string): Employee[] => {
  return employees.filter((emp) => emp.subDepartmentDesc === subDepartment);
};

export const getManagerChain = (employees: Employee[], employeeCode: string): Employee[] => {
  const chain: Employee[] = [];
  const employee = getEmployeeByCode(employees, employeeCode);
  
  if (!employee) {
    return chain;
  }

  let currentCode = employee.supervisorCode;
  const visited = new Set<string>();

  while (currentCode && !visited.has(currentCode)) {
    visited.add(currentCode);
    const manager = getEmployeeByCode(employees, currentCode);
    
    if (!manager) {
      break;
    }

    chain.push(manager);
    currentCode = manager.supervisorCode;
  }

  return chain;
};
